import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Input, Message } from "semantic-ui-react";
import { RedirectIfLoggedIn } from "../Login/RedirectIfLoggedIn";
import { ReactComponent as Logo } from "../../icons/Logo text_vertical.svg";
import { ReactComponent as BMIMILogo } from "../../icons/BMIMI_Logo_srgb.svg";
import { useIntl } from "react-intl";
import { checkPasswordResetToken, resetPassword } from "../../api/users";
import { LanguageSelector } from "../../components/LanguageSelector/LanguageSelector";
import "../Login/Login.css";

export const ResetPassword = () => {
    const intl = useIntl();
    const [password, setPassword] = useState("");
    const [repeatedPassword, setRepeatedPassword] = useState("");
    const [validToken, setValidToken] = useState(false);
    const [recoveryToken, setRecoveryToken] = useState("");
    const location = useLocation();
    const [success, setSuccess] = useState(false);
    const [sendingRequest, setSendingRequest] = useState(false);
    const [type, setType] = useState("text");
    const [errors, setErrors] = useState<string[]>([]);

    const handleCheckResetPasswordToken = async (token: string) => {
        try {
            setRecoveryToken(token);
            await checkPasswordResetToken(token);
            setValidToken(true);
        } catch {}
    };

    const handleResetPassword = async () => {
        setSendingRequest(true);
        try {
            await resetPassword({ token: recoveryToken, password });
            setSuccess(true);
        } catch {
        } finally {
            setSendingRequest(false);
        }
    };

    const handleType = (e: any) => {
        if (e._reactName === "onBlur" && e.target.value === "") setType("text");
        else setType("password");
    };

    useEffect(() => {
        handleCheckResetPasswordToken(location.search.split("=")[1] ?? "");
    }, [location.search]);

    useEffect(() => {
        if (password.length > 0) {
            if (password.length < 8)
                setErrors(x =>
                    !x.some(e => e === "passwordTooShort") ? x.concat("passwordTooShort") : x
                );
            else setErrors(x => x.filter(e => e !== "passwordTooShort"));
            if (!/\d/.test(password))
                setErrors(x =>
                    !x.some(e => e === "passwordNoNumber") ? x.concat("passwordNoNumber") : x
                );
            else setErrors(x => x.filter(e => e !== "passwordNoNumber"));
            if (!/[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password))
                setErrors(x =>
                    !x.some(e => e === "passwordNoSpecialCharacter")
                        ? x.concat("passwordNoSpecialCharacter")
                        : x
                );
            else setErrors(x => x.filter(e => e !== "passwordNoSpecialCharacter"));
            if (!!password && !!repeatedPassword && password !== repeatedPassword)
                setErrors(x =>
                    !x.some(e => e === "passwordDontMatch") ? x.concat("passwordDontMatch") : x
                );
            else setErrors(x => x.filter(e => e !== "passwordDontMatch"));
        } else setErrors([]);
    }, [password, repeatedPassword]);

    return (
        <div className="login-container">
            <RedirectIfLoggedIn />
            <div className="login-wrapper">
                <div className="center-login-container">
                    <section className="left-container">
                        <div className="upper-container"></div>
                        <div className="lower-container"></div>
                        <Logo title="" />
                    </section>
                    <section className="login-form-container">
                        {success ? (
                            <>
                                <p>{intl.formatMessage({ id: "passwordSuccessReset" })}</p>
                                <Button
                                    fluid
                                    primary
                                    as={Link}
                                    to="/login"
                                    content={intl.formatMessage({ id: "backToLogin" })}
                                />
                            </>
                        ) : validToken ? (
                            <>
                                <h2 id="centered-text">
                                    {intl.formatMessage({ id: "resetPassword" })}
                                </h2>
                                <Input
                                    type={type}
                                    placeholder={intl.formatMessage({ id: "password" })}
                                    value={password}
                                    onChange={(e: any) => setPassword(e.target.value)}
                                    autoComplete="new-password"
                                    onFocus={handleType}
                                    onBlur={handleType}
                                />
                                <Input
                                    type={type}
                                    placeholder={intl.formatMessage({ id: "repeatedPassword" })}
                                    value={repeatedPassword}
                                    onChange={(e: any) => setRepeatedPassword(e.target.value)}
                                    autoComplete="new-password"
                                    onFocus={handleType}
                                    onBlur={handleType}
                                />
                                <LanguageSelector />
                                <section className="password-reset-errors">
                                    {errors.length > 0 && (
                                        <Message
                                            className="error-message"
                                            icon="exclamation circle"
                                            content={errors.map(e => (
                                                <span key={e}>{intl.formatMessage({ id: e })}</span>
                                            ))}
                                        />
                                    )}
                                </section>
                                <hr />
                                <Button
                                    fluid
                                    primary
                                    loading={sendingRequest}
                                    disabled={!password || !repeatedPassword || errors.length > 0}
                                    content={intl.formatMessage({ id: "resetPassword" })}
                                    onClick={handleResetPassword}
                                />
                            </>
                        ) : (
                            <p id="login-error-message">
                                {intl.formatMessage({ id: "passwordResetToken" })}
                            </p>
                        )}
                    </section>
                </div>
                <BMIMILogo title="" />
            </div>
        </div>
    );
};
